import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

export default function LoginPage() {
  const { login, register, startGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        toast.success("Se l'email è registrata, ti abbiamo inviato il link di reset.");
        setMode("login");
        return;
      }
      const user = mode === "login" ? await login(email, password) : await register(name, email, password);
      toast.success(mode === "login" ? "Bentornata/o!" : "Account creato!");
      navigate(user.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const titles = {
    login: ["Accedi", "Bentornata/o su NEED."],
    register: ["Crea account", "Bastano pochi secondi."],
    forgot: ["Recupera password", "Ti invieremo un link per reimpostarla."],
  };

  return (
    <div className="safe-pt grid min-h-screen grid-cols-1 bg-cream md:grid-cols-2">
      <div className="relative hidden overflow-hidden md:block">
        <img
          src="https://images.unsplash.com/photo-1583329550487-0fa300a4cd1a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="NEED" className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-ink/30" />
        <Link to="/" className="notranslate absolute left-8 top-8 font-serif text-4xl text-cream" translate="no">NEED</Link>
        <p className="absolute bottom-10 left-8 right-8 font-serif text-3xl font-light leading-tight text-cream">
          Il necessario, ogni mese. Accedi per i tuoi preferiti e il Box Mensile.
        </p>
      </div>

      <div className="flex flex-col justify-center px-6 py-12 md:px-16">
        <Link to="/" data-testid="back-home" className="mb-10 inline-flex items-center gap-2 text-sm text-ink-soft hover:text-ink">
          <ArrowLeft className="h-4 w-4" /> Torna al negozio
        </Link>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mx-auto w-full max-w-sm">
          <h1 className="font-serif text-4xl font-light text-ink">{titles[mode][0]}</h1>
          <p className="mt-2 text-sm text-ink-soft">{titles[mode][1]}</p>

          {mode !== "forgot" && (
            <>
              <button
                onClick={startGoogleLogin}
                data-testid="google-login-button"
                className="mt-8 flex w-full items-center justify-center gap-3 rounded-full border border-ink/15 bg-panel py-3.5 text-sm font-medium text-ink transition-colors hover:bg-ink/5"
              >
                <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="h-5 w-5" />
                Continua con Google
              </button>

              <div className="my-6 flex items-center gap-4 text-xs uppercase tracking-overline text-ink-soft">
                <span className="h-px flex-1 bg-ink/10" /> oppure <span className="h-px flex-1 bg-ink/10" />
              </div>
            </>
          )}

          <form onSubmit={submit} className={`space-y-3 ${mode === "forgot" ? "mt-8" : ""}`}>
            {mode === "register" && (
              <input
                value={name} onChange={(e) => setName(e.target.value)} required placeholder="Nome"
                data-testid="name-input"
                className="w-full rounded-xl border border-ink/15 bg-panel px-4 py-3 text-sm outline-none focus:border-sage"
              />
            )}
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email"
              data-testid="email-input"
              className="w-full rounded-xl border border-ink/15 bg-panel px-4 py-3 text-sm outline-none focus:border-sage"
            />
            {mode !== "forgot" && (
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Password" minLength={6}
                data-testid="password-input"
                className="w-full rounded-xl border border-ink/15 bg-panel px-4 py-3 text-sm outline-none focus:border-sage"
              />
            )}
            {mode === "login" && (
              <button type="button" onClick={() => { setMode("forgot"); setError(""); }} data-testid="forgot-link" className="block text-left text-sm text-sage hover:underline">
                Password dimenticata?
              </button>
            )}
            {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
            <button
              type="submit" disabled={loading} data-testid="submit-auth-button"
              className="w-full rounded-full bg-ink py-3.5 text-sm font-semibold text-cream transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60"
            >
              {loading ? "Attendi…" : mode === "login" ? "Accedi" : mode === "register" ? "Registrati" : "Invia link di reset"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-ink-soft">
            {mode === "forgot" ? (
              <button onClick={() => { setMode("login"); setError(""); }} data-testid="back-to-login" className="font-semibold text-sage hover:underline">
                Torna all'accesso
              </button>
            ) : (
              <>
                {mode === "login" ? "Non hai un account?" : "Hai già un account?"}{" "}
                <button
                  onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
                  data-testid="toggle-auth-mode"
                  className="font-semibold text-sage hover:underline"
                >
                  {mode === "login" ? "Registrati" : "Accedi"}
                </button>
              </>
            )}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
